import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import '../styles/ContactCTA.css';

gsap.registerPlugin(ScrollTrigger);

const initialForm = {
  name: '',
  company: '',
  email: '',
  phone: '',
  message: '',
};

export default function ContactCTA() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.cta__card', {
        y: 60,
        opacity: 0,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.cta-section',
          start: 'top 70%',
        },
      });
    }, ref);
    return () => ctx.revert();
  }, []);


  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!form.company.trim()) e.company = 'Required';
    if (!form.email.trim()) e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email';
    if (!form.message.trim()) e.message = 'Required';
    return e;
  };

  const handleChange = (field) => (e) => {
    setForm(p => ({ ...p, [field]: e.target.value }));
    if (errors[field]) setErrors(p => ({ ...p, [field]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      const firstErr = document.querySelector('.cta__field--err .cta__input');
      if (firstErr) {
        gsap.fromTo(firstErr, { x: -6 }, { x: 6, duration: 0.08, repeat: 5, yoyo: true, ease: 'power2.inOut', onComplete: () => gsap.set(firstErr, { x: 0 }) });
      }
      return;
    }
    setSending(true);
    setSendError('');

    try {
      const res = await fetch('https://formsubmit.co/ajax/info@cvearity.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          name: form.name,
          company: form.company,
          email: form.email,
          phone: form.phone || 'Not provided',
          message: form.message,
          _subject: `New Contact from ${form.name} - CVEarity Website`,
          _template: 'table',
        }),
      });

      const data = await res.json();
      if (data.success === 'true' || data.success === true || res.ok) {
        setSent(true);
      } else {
        setSendError('Something went wrong. Please try again.');
      }
    } catch {
      setSendError('Network error. Please try again later.');
    } finally {
      setSending(false);
    }
  };

  const handleFocus = (e) => {
    gsap.to(e.target, { boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.15), 0 0 20px rgba(59, 130, 246, 0.08)', duration: 0.3 });
  };

  const handleBlur = (e) => {
    gsap.to(e.target, { boxShadow: 'none', duration: 0.3 });
  };

  return (
    <section id="contact" className="cta-section section">

      <div className="container">
        <motion.div
          ref={ref}
          className="cta__head"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.45 }}
        >
          <div className="section-eyebrow">Get in Touch</div>
          <h2 className="cta__h2">Have questions about CVEarity?</h2>
          <p className="cta__sub">
            We're here to help secure your infrastructure.
          </p>
        </motion.div>

        <motion.div
          className="cta__card"
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.55, delay: 0.1 }}
        >
          <div className="cta__logo-panel">
            <img
              src={process.env.PUBLIC_URL + '/CVEarity with Transparent BG.png'}
              alt="CVEarity"
              className="cta__logo-img"
            />
            <p className="cta__logo-tagline">
              Advanced vulnerability intelligence for modern security teams
            </p>
            <div className="cta__logo-features">
              <div className="cta__logo-feature">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 8L6 12L14 4" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                <span>24/7 Monitoring</span>
              </div>
              <div className="cta__logo-feature">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 8L6 12L14 4" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                <span>Real-time Alerts</span>
              </div>
              <div className="cta__logo-feature">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 8L6 12L14 4" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                <span>Expert Support</span>
              </div>
            </div>
          </div>

          <div className="cta__form-panel">
            <AnimatePresence mode="wait">
              {sent ? (
                <motion.div
                  className="cta__success"
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="cta__success-icon">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                      <circle cx="20" cy="20" r="19" stroke="#3b82f6" strokeWidth="1.5" className="cta__success-circle"/>
                      <path d="M11 20L17 26L29 14" stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="cta__success-check"/>
                    </svg>
                  </div>
                  <h3 className="cta__success-title">Message Sent!</h3>
                  <p className="cta__success-sub">We'll get back to you within one business day.</p>
                  <button className="btn-secondary cta__again" onClick={() => { setSent(false); setForm(initialForm); }}>
                    Send another
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  className="cta__form"
                  onSubmit={handleSubmit}
                  noValidate
                  initial={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <div className={`cta__field ${errors.name ? 'cta__field--err' : ''}`}>
                    <label className="cta__label" htmlFor="cta-name">Name</label>
                    <input
                      id="cta-name"
                      className="cta__input"
                      type="text"
                      placeholder="Enter your name"
                      value={form.name}
                      onChange={handleChange('name')}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      autoComplete="name"
                    />
                    {errors.name && <span className="cta__err">{errors.name}</span>}
                  </div>

                  <div className={`cta__field ${errors.company ? 'cta__field--err' : ''}`}>
                    <label className="cta__label" htmlFor="cta-company">Company Name</label>
                    <input
                      id="cta-company"
                      className="cta__input"
                      type="text"
                      placeholder="Enter your company name"
                      value={form.company}
                      onChange={handleChange('company')}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                    {errors.company && <span className="cta__err">{errors.company}</span>}
                  </div>

                  <div className={`cta__field ${errors.email ? 'cta__field--err' : ''}`}>
                    <label className="cta__label" htmlFor="cta-email">Company Email</label>
                    <input
                      id="cta-email"
                      className="cta__input"
                      type="email"
                      placeholder="Enter your company email"
                      value={form.email}
                      onChange={handleChange('email')}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      autoComplete="email"
                    />
                    {errors.email && <span className="cta__err">{errors.email}</span>}
                  </div>

                  <div className="cta__field">
                    <label className="cta__label" htmlFor="cta-phone">Phone No.</label>
                    <input
                      id="cta-phone"
                      className="cta__input"
                      type="tel"
                      placeholder="Enter your phone number"
                      value={form.phone}
                      onChange={handleChange('phone')}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      autoComplete="tel"
                    />
                  </div>

                  <div className={`cta__field ${errors.message ? 'cta__field--err' : ''}`}>
                    <label className="cta__label" htmlFor="cta-message">Queries if you have any:</label>
                    <textarea
                      id="cta-message"
                      className="cta__input cta__textarea"
                      placeholder="Tell us about your security needs and any questions you have..."
                      value={form.message}
                      onChange={handleChange('message')}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      rows={4}
                    />
                    {errors.message && <span className="cta__err">{errors.message}</span>}
                  </div>

                  {sendError && <div className="cta__send-error">{sendError}</div>}

                  <button type="submit" className="cta__submit" disabled={sending}>
                    {sending ? <span className="cta__spinner" /> : (
                      <>
                        Send Request
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M2 8H14M14 8L9 3M14 8L9 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </>
                    )}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
